import React, {
  useMemo,
  useState,
  useEffect,
  useContext,
  useRef,
} from 'react';
import moment from 'moment';
import intl from 'react-intl-universal';
import { createPortal } from 'react-dom';
import ReactSVG from 'react-svg';
import { inject, observer } from 'mobx-react';
import { IImageMayBe } from 'global';

import NoX from '../Common/NoX';
import SearchRow from './SearchRow';
import {
  DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH,
} from './SearchRow';
import { alpha } from '../../utils/color';
import ReturnIcon from '../Common/ReturnIcon';
import RootStore from '../../store/RootStore';
import galleryApi from '../../actions/gallery';
import SimpleBigPic from '../BigPic/SimpleBigPic';
import GalleryJumperWithName from '../BigPic/GalleryJumperWithName';
import useSimpleBigPic from '../BigPic/useSimpleBigPic';
import AllForBigPicPersonList from '../BigPic/AllForBigPicPersonList';
import AllForColumn2 from '../GalleryList/AllForColumn2';
import { ThemeContext } from '../../store/ThemeProvider';
import JigsawBottom from '../ComponentNavigator/JigsawBottom';
import PosterPortal from '../ComponentNavigator/PosterPortal';
import { CloseAnimation } from '../GalleryList/GalleryListWrapper';
import { maxNumberByType } from '../ComponentNavigator/jigsaw-limits';
import { pushHistory, popHistory, registerHistory } from '../../utils/history';
import MayBeOneImg from '../Me/MayBeOneImg';
import ComponentLink from '../ComponentLink';
import {
  ExhibitorItem,
  IExhibitorItem,
} from '../ComponentExhibitor/AllForExhibitor';

import './SearchResult.scss';

export { GalleryJumperWithName };


interface ISearchResult {
  close: () => void;
  keyword?: string;
  mode?: 'text' | 'image';
  imagePreview?: string;
  list: any[];
  loading?: boolean;
  onReselectImage?: (base64: string, file?: File) => void;
  onSearchKeyword?: (keyword: string) => void;
  placeholder?: string;
  imageSearchText?: string;
  searchButtonText?: string;
  loadingText?: string;
  noResultText?: string;
  recognizedCountText?: string;
  similarCountText?: string;
  batchDownloadText?: string;
  enableImageSearch?: boolean;
  imageResizeWidth?: number;
  searchLayout?: 'default' | 'button';
  showGalleryJumper?: boolean;
  exhibitors?: IExhibitorItem[];
  exhibitorTotal?: number;
  onExhibitorNavigate?: () => void;
  searchScope?: 'all' | 'current_first' | 'specified';
  currentGalleryId?: string | number;
  searchRangeNames?: string[];
  vectorSearchEnabled?: boolean;
  rootStore?: RootStore;
}

const formatTemplate = (
  template: string,
  params: Record<string, string | number> = {}
) => {
  return Object.keys(params).reduce((result, key) => {
    return result.replace(new RegExp(`\\{\\s*${key}\\s*\\}`, 'g'), String(params[key]));
  }, String(template || ''));
};

const containerEl = document.createElement('div');
const SearchResult = function (props: ISearchResult) {
  const [galleryMap, setGalleryMap] = useState<Record<string, any>>({});
  const [current, setCurrent] = useState(-1);
  const { keyword, mode = 'text', imagePreview, loading = false } = props;
  const [searchInput, setSearchInput] = useState(props.keyword || '');
  const [maybeMap, setMaybeMap] = useState<Record<string, IImageMayBe[]>>({});
  const [activeAvatarIndex, setActiveAvatarIndex] = useState(0);
  const [personParams, setPersonParams] = useState({
    personid: '',
    imageid: '',
    faceurl: '',
  });
  const personHistoryActiveRef = useRef(false);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const imageSectionRef = useRef<HTMLElement | null>(null);
  const currentAlbumSectionRef = useRef<HTMLDivElement | null>(null);
  const otherAlbumSectionRef = useRef<HTMLDivElement | null>(null);
  const [showAllExhibitors, setShowAllExhibitors] = useState(false);
  const [imageSectionVisible, setImageSectionVisible] = useState(false);
  const [posterImage, setPosterImage] = useState<any | null>(null);
  const [activeAlbumTab, setActiveAlbumTab] = useState<'current' | 'other'>('current');

  useEffect(() => {
    document.body.appendChild(containerEl);
    return () => {
      document.body.removeChild(containerEl);
    };
  }, []);

  useEffect(() => {
    const closeSearchResult = () => {
      props.close();
    };

    // 从展商详情整页返回时，当前 entry 已经是 #search-result，不能再次 push，
    // 只需恢复它的关闭回调即可。
    if (window.location.hash === '#search-result') {
      registerHistory('search-result', closeSearchResult);
      return;
    }

    pushHistory('search-result', closeSearchResult);
  }, []);

  useEffect(() => {
    //这个弹窗显示的时候，隐藏掉Video
    window.dispatchEvent(new Event('hide-video'));

    const activeElement = document.activeElement as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLElement
      | null;
    if (activeElement && typeof activeElement.blur === 'function') {
      activeElement.blur();
    }
  }, []);

  useEffect(() => {
    if (mode === 'text') {
      setSearchInput(keyword || '');
      return;
    }

    if (mode === 'image') {
      setSearchInput('');
    }
  }, [keyword, mode]);

  const [jigsaw, _setJigsaw] = useState({ bottomShow: false, jigsawList: [] as any[], type: '' });
  // merge 语义，与 MobX setJigsaw 保持一致
  const setLocalJigsaw = (params: Record<string, any>) => _setJigsaw(prev => ({ ...prev, ...params }));

  const { componentColor, h1Color, h2Color, mainColor } = useContext(ThemeContext);

  const exhibitors = props.exhibitors || [];
  const displayedExhibitors = showAllExhibitors
    ? exhibitors
    : exhibitors.slice(0, 3);
  const hasLongVisibleExhibitorList =
    showAllExhibitors && displayedExhibitors.length > 3;
  const shouldShowBatchDownload =
    !hasLongVisibleExhibitorList || imageSectionVisible;

  useEffect(() => {
    setShowAllExhibitors(false);
  }, [keyword, mode]);

  useEffect(() => {
    const target = imageSectionRef.current;
    const overlay = overlayRef.current;
    if (!target || !overlay) {
      setImageSectionVisible(false);
      return;
    }

    const updateVisibility = () => {
      const imageTop = target.getBoundingClientRect().top;
      const overlayTop = overlay.getBoundingClientRect().top;
      setImageSectionVisible(imageTop - overlayTop <= 140);
    };
    updateVisibility();
    overlay.addEventListener('scroll', updateVisibility, { passive: true });
    window.addEventListener('resize', updateVisibility);
    return () => {
      overlay.removeEventListener('scroll', updateVisibility);
      window.removeEventListener('resize', updateVisibility);
    };
  }, [loading, props.list.length, exhibitors.length, showAllExhibitors]);

  useEffect(() => {
    const galleryIds = Array.from(
      new Set(
        (props.list || [])
          .map((item: any) => String(item?.galleryid || item?.gallery_id || ''))
          .filter(Boolean)
      )
    );

    if (!galleryIds.length) {
      setGalleryMap({});
      return;
    }

    let cancelled = false;

    galleryApi.batch(galleryIds).then((res: any) => {
      if (cancelled) {
        return;
      }

      const data = Array.isArray(res?.data) ? res.data : [];
      const nextMap = data.reduce((result: Record<string, any>, item: any) => {
        if (item && item.id) {
          result[String(item.id)] = item;

          if (item.watermarks) {
            props.rootStore?.galleryStore.setWatermarksByKey(
              String(item.id),
              item.watermarks
            );
          }
        }
        return result;
      }, {});

      if (window.gallery?.id) {
        nextMap[String(window.gallery.id)] = {
          ...window.gallery,
          id: window.gallery.id,
          link_id: window.pageDetail?.link_id,
          url_labeling: window.gallery?.url_labeling,
          name: window.gallery?.name,
        };
      }

      setGalleryMap(nextMap);
    }).catch((error: any) => {
      console.warn('[SearchResult] gallery batch failed', error);
    });

    return () => {
      cancelled = true;
    };
  }, [props.list]);

  const displayList = useMemo(() => {
    console.log(props);
    return (props.list || []).map((item: any) => {
      const normalizedGalleryId = item.galleryid || item.gallery_id;
      const galleryInfo = galleryMap[String(normalizedGalleryId)] || {};
      const linkId = galleryInfo.link_id || galleryInfo.link || item.link_id;
      const urlLabeling = galleryInfo.url_labeling || item.url_labeling;
      return {
        ...item,
        galleryid: normalizedGalleryId || galleryInfo.id,
        gallery_id: normalizedGalleryId || galleryInfo.id,
        galleryname:
          item.galleryname || galleryInfo.name || galleryInfo.galleryname,
        link:
          linkId || urlLabeling
            ? {
                type: 'gallery',
                value: String(normalizedGalleryId || galleryInfo.id || ''),
                link_id: linkId,
                url_labeling: urlLabeling,
              }
            : item.link,
      };
    });
  }, [galleryMap, props.list]);

  const isCurrentGalleryFirst =
    props.searchScope === 'current_first' && !!props.currentGalleryId;
  const currentGalleryId = String(props.currentGalleryId || '');
  const orderedDisplayList = useMemo(() => {
    if (!isCurrentGalleryFirst) {
      return displayList;
    }
    const currentItems = displayList.filter(
      (item: any) => String(item.galleryid || item.gallery_id || '') === currentGalleryId
    );
    const otherItems = displayList.filter(
      (item: any) => String(item.galleryid || item.gallery_id || '') !== currentGalleryId
    );
    return currentItems.concat(otherItems);
  }, [displayList, isCurrentGalleryFirst, currentGalleryId]);

  const { list, columnsClick, setBigIndex, bigIndex, loadBigPic, loadFinish } =
    useSimpleBigPic(orderedDisplayList);

  const currentAlbumList = isCurrentGalleryFirst
    ? list.filter((item: any) => String(item.galleryid || item.gallery_id || '') === currentGalleryId)
    : list;
  const otherAlbumList = isCurrentGalleryFirst
    ? list.filter((item: any) => String(item.galleryid || item.gallery_id || '') !== currentGalleryId)
    : [];

  useEffect(() => {
    const overlay = overlayRef.current;
    if (!isCurrentGalleryFirst || !overlay) {
      setActiveAlbumTab('current');
      return;
    }

    const updateActiveAlbumTab = () => {
      const otherSection = otherAlbumSectionRef.current;
      if (!otherSection) {
        setActiveAlbumTab('current');
        return;
      }
      const overlayTop = overlay.getBoundingClientRect().top;
      const otherTop = otherSection.getBoundingClientRect().top;
      setActiveAlbumTab(otherTop - overlayTop <= 150 ? 'other' : 'current');
    };

    updateActiveAlbumTab();
    overlay.addEventListener('scroll', updateActiveAlbumTab, { passive: true });
    return () => overlay.removeEventListener('scroll', updateActiveAlbumTab);
  }, [isCurrentGalleryFirst, currentAlbumList.length, otherAlbumList.length]);

  const scrollToAlbumSection = (target: 'current' | 'other') => {
    const overlay = overlayRef.current;
    const section = target === 'current'
      ? currentAlbumSectionRef.current
      : otherAlbumSectionRef.current;
    if (!overlay || !section) return;
    const top = section.getBoundingClientRect().top
      - overlay.getBoundingClientRect().top
      + overlay.scrollTop
      - 118;
    overlay.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  };

  useEffect(() => {
    if (bigIndex === -1) {
      const timer = setTimeout(() => {
        setCurrent(bigIndex);
      }, 10);
      return () => clearTimeout(timer);
    }

    const timer = setTimeout(() => {
      setCurrent(bigIndex);
    }, 300);
    return () => clearTimeout(timer);
  }, [bigIndex]);

  const currentGallery = bigIndex > -1 ? list[bigIndex] : null;
  const currentMaybeList =
    currentGallery && currentGallery.id ? maybeMap[String(currentGallery.id)] || [] : [];

  const gallery = useMemo(()=>{
    if(!currentGallery) {
      return null;
    }
    const currentGalleryId = currentGallery.galleryid || currentGallery.gallery_id;
    return galleryMap[String(currentGalleryId)] || {};
  },[galleryMap, list, bigIndex]);

  useEffect(() => {
    setActiveAvatarIndex(0);
  }, [bigIndex]);

  useEffect(() => {
    const imageId = currentGallery && currentGallery.id;
    if (!Number(window.configDetail?.use_face) || !imageId || maybeMap[String(imageId)]) {
      return;
    }

    let cancelled = false;
    props.rootStore!.wxuserStore.faceMaybe(imageId).then((data: any) => {
      if (cancelled) {
        return;
      }
      setMaybeMap(prev => ({
        ...prev,
        [String(imageId)]: Array.isArray(data) ? data : [],
      }));
    }).catch((error: any) => {
      console.warn('[SearchResult] face maybe failed', error);
      if (!cancelled) {
        setMaybeMap(prev => ({
          ...prev,
          [String(imageId)]: [],
        }));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [currentGallery && currentGallery.id, maybeMap, props.rootStore]);

  const onPersonClose = () => {
    personHistoryActiveRef.current = false;
    setPersonParams({
      personid: '',
      imageid: '',
      faceurl: '',
    });
  };

  const onPersonBack = () => {
    if (personHistoryActiveRef.current) {
      popHistory();
      return;
    }

    onPersonClose();
  };

  const onPersonClick = (
    index: number,
    maybe: IImageMayBe,
    faceurl?: string
  ) => {
    setActiveAvatarIndex(index);

    if (!personHistoryActiveRef.current) {
      personHistoryActiveRef.current = true;
      pushHistory('search-result-person', onPersonClose);
    }

    setPersonParams({
      personid: String(maybe.personid || ''),
      imageid: String(maybe.imageid || ''),
      faceurl: faceurl || '',
    });
  };

  const onImageClick = ({
    index,
    id,
    galleryid,
  }: {
    index?: number;
    id: number;
    galleryid?: string | number;
  }) => {
    const realIndex = list.findIndex(
      (one: any) => Number(one.id) === Number(id)
    );
    if (jigsaw.bottomShow) {
      const nextList = [...jigsaw.jigsawList];
      const currentIndex = nextList.findIndex(
        (one: any) => Number(one.id) === Number(id)
      );

      if (currentIndex > -1) {
        nextList.splice(currentIndex, 1);
      } else {
        if (nextList.length >= maxNumberByType(jigsaw.type)) {
          props.rootStore?.uiStore.showAlert({
            content: intl.get('MAX_DOWNLOAD_NUM', {
              num: maxNumberByType(jigsaw.type),
            }),
          });
          return;
        }

        const currentItem = list[realIndex];
        if (currentItem) {
          nextList.push(currentItem);
        }
      }

      setLocalJigsaw({ jigsawList: nextList });
      return;
    }

    columnsClick({ index, id });
  };

  const handleClose = () => {
    setLocalJigsaw({ bottomShow: false, jigsawList: [], type: '' });
    popHistory();
  };

  const startDownload = () => {
    setLocalJigsaw({
      bottomShow: true,
      jigsawList: [],
      type: 'download',
    });
  };

  const onExhibitorClick = (item: IExhibitorItem) => {
    props.onExhibitorNavigate?.();
    ComponentLink.to(`/page/exhibitor/${item.id}`);
  };

  const triggerKeywordSearch = () => {
    const nextKeyword = (searchInput || '').trim();
    if (!nextKeyword || !props.onSearchKeyword) {
      return;
    }
    props.onSearchKeyword(nextKeyword);
  };

  const triggerImageSearch = (base64: string, file?: File) => {
    setSearchInput('');
    props.onReselectImage?.(base64, file);
  };

  const zipFileName = `${(
    keyword || (mode === 'image' ? '图片搜索结果' : '搜索结果')
  ).replace(/[\\/:*?"<>|\s]+/g, '_')}_${moment().format(
    'YYYY_MM_DD_HH_mm_ss'
  )}.zip`;

  // 智能搜图（含上传图片搜索）使用“高相似图片”文案；OCR/识别信息使用普通结果文案。
  const shouldShowSimilarImageText = mode === 'image' || !!props.vectorSearchEnabled;

  const countText = shouldShowSimilarImageText
    ? formatTemplate(
        props.similarCountText || '当前展示 {count} 张高相似图',
        { count: list.length }
      )
    : formatTemplate(
        props.recognizedCountText || '共识别出 {count} 张图片',
        { count: list.length }
      );

  const resultText = loading
    ? props.loadingText || '正在搜索中...'
    : list.length > 0
    ? countText
    : props.noResultText || '未找到相关图片';

  return createPortal(
    <div
      ref={overlayRef}
      className="search-result-overlay"
      style={{ backgroundColor: alpha(componentColor, 1) }}
    >
      <div
        className="search-result"
        style={{ backgroundColor: alpha(componentColor, 1) }}
      >
        <div className="title" style={{ color: h1Color, display: 'none' }}>
          {/* {mode === 'image' ? '图片搜索结果' : '文字搜索结果'} */}
        </div>
        <div className="search-result-top-search">
          <SearchRow
            value={searchInput}
            onInputChange={setSearchInput}
            placeholder={
              props.placeholder ||
              intl.get('Enter your participate no., letters included')
            }
            search={triggerKeywordSearch}
            searchByImage={triggerImageSearch}
            enableImageSearch={props.enableImageSearch}
            imageSearchText={props.imageSearchText}
            searchButtonText={props.searchButtonText}
            imageResizeWidth={
              props.imageResizeWidth || DEFAULT_IMAGE_SEARCH_RESIZE_WIDTH
            }
            searchLayout={props.searchLayout || 'default'}
          />
        </div>
        <div className="search-result-header">
          {mode === 'image' && imagePreview ? (
            <div className="search-result-summary">
              <img src={imagePreview} alt="search preview" />
              <div className="search-result-summary-content">
                <div className="search-result-summary-text">{resultText}</div>
                {!loading ? (
                  <div className="search-result-link-btn" style={{ display: 'none' }}>
                    点击上方相机图标可重新上传
                  </div>
                ) : null}
              </div>
            </div>
          ) : (
            <>
              {keyword ? (
                <div className="search-result-keyword">{keyword}</div>
              ) : null}
            </>
          )}
        </div>
        {!loading && exhibitors.length > 0 ? (
          <section className="search-result-section search-result-exhibitors">
            <div className="search-result-section-title" style={{ color: h1Color }}>
              <i style={{ backgroundColor: mainColor }} />
              相关展商（{props.exhibitorTotal || exhibitors.length}个）
            </div>
            <div className="search-result-exhibitor-list">
              {displayedExhibitors.map(item => (
                <ExhibitorItem
                  key={item.id}
                  item={item}
                  onClick={onExhibitorClick}
                  h1Color={h1Color}
                  h2Color={h2Color}
                  mainColor={mainColor}
                  showBooth
                  showTopTag
                />
              ))}
            </div>
            {exhibitors.length > 3 ? (
              <button
                type="button"
                className="search-result-exhibitor-toggle"
                style={{ color: mainColor, backgroundColor: alpha(mainColor, 0.08) }}
                onClick={() => setShowAllExhibitors(value => !value)}
              >
                <div className="search-result-exhibitor-toggle-content">
                  <span>{showAllExhibitors ? '收起展商结果' : '查看全部展商'}</span>
                  <ReactSVG
                    src={require('../../svg/up-angle.svg')}
                    className={`search-result-exhibitor-toggle-icon${
                      showAllExhibitors ? ' is-expanded' : ''
                    }`}
                  />
                </div>
              </button>
            ) : null}
          </section>
        ) : null}
        <section ref={imageSectionRef} className="search-result-section search-result-images">
          {!loading ? (
            <>
              <div className="search-result-section-title" style={{ color: h1Color }}>
                <i style={{ backgroundColor: mainColor }} />
                {shouldShowSimilarImageText ? '高相似图片' : '图片结果'}（{list.length}张）
              </div>
              {props.searchScope === 'specified' && (props.searchRangeNames || []).length ? (
                <div className="search-result-scope-text" style={{ color: h2Color }}>
                  搜索范围：{(props.searchRangeNames || []).join(' | ')}
                </div>
              ) : null}
              {isCurrentGalleryFirst ? (
                <div
                  className="search-result-gallery-tabs"
                  style={{ backgroundColor: alpha(componentColor, 1) }}
                >
                  <button
                    type="button"
                    className={activeAlbumTab === 'current' ? 'active' : ''}
                    style={activeAlbumTab === 'current' ? { color: h1Color } : { color: h2Color }}
                    onClick={() => scrollToAlbumSection('current')}
                  >
                    当前相册&nbsp; {currentAlbumList.length}
                    {activeAlbumTab === 'current' ? <i style={{ backgroundColor: mainColor }} /> : null}
                  </button>
                  <button
                    type="button"
                    className={activeAlbumTab === 'other' ? 'active' : ''}
                    style={activeAlbumTab === 'other' ? { color: h1Color } : { color: h2Color }}
                    onClick={() => scrollToAlbumSection('other')}
                  >
                    其他相册&nbsp; {otherAlbumList.length}
                    {activeAlbumTab === 'other' ? <i style={{ backgroundColor: mainColor }} /> : null}
                  </button>
                </div>
              ) : null}
            </>
          ) : null}
        {loading ? (
          <div className="search-result-loading">{props.loadingText || '正在搜索中...'}</div>
        ) : list.length === 0 ? (
          <NoX text={resultText} />
        ) : isCurrentGalleryFirst ? (
          <>
            <div ref={currentAlbumSectionRef} className="gallery-list search-result-current-gallery-list">
              <AllForColumn2
                list={currentAlbumList}
                onClick={onImageClick}
                jigsaw={jigsaw}
                lazy={false}
                current={bigIndex}
              />
            </div>
            <div className="search-result-other-divider" style={{ color: h2Color }}>
              <span />
              以下为其他相册搜索结果
              <span />
            </div>
            <div ref={otherAlbumSectionRef} className="search-result-other-gallery-section">
              <div className="search-result-section-title" style={{ color: h1Color }}>
                其他相册结果（{otherAlbumList.length}张）
              </div>
              <div className="gallery-list search-result-other-gallery-list">
                <AllForColumn2
                  list={otherAlbumList}
                  onClick={onImageClick}
                  jigsaw={jigsaw}
                  lazy={false}
                  current={bigIndex}
                />
              </div>
            </div>
            <CloseAnimation
              containerSelectorFunction={() => '.search-result'}
              targetElementSelectorFunction={(item: any) =>
                `.search-result .img-wrapper[data-id="${item.id}"]`
              }
              list={list}
              index={bigIndex}
            />
          </>
        ) : (
          <div className="gallery-list">
            <AllForColumn2
              list={list}
              onClick={onImageClick}
              jigsaw={jigsaw}
              lazy={false}
              current={bigIndex}
            />
            <CloseAnimation
              containerSelectorFunction={() => '.search-result'}
              targetElementSelectorFunction={(item: any) =>
                `.search-result .img-wrapper[data-id="${item.id}"]`
              }
              list={list}
              index={bigIndex}
            />
          </div>
        )}
        </section>
        {!loading && jigsaw.bottomShow ? (
          <JigsawBottom
            type="download"
            list={jigsaw.jigsawList}
            zipFileName={zipFileName}
            onCancel={() => {
              setLocalJigsaw({ bottomShow: false, jigsawList: [], type: '' });
            }}
            onConfirm={() => {}}
          />
        ) : null}
        <div className="search-result-return-fixed">
          <ReturnIcon onClick={handleClose} />
        </div>

        {!loading &&
        list.length > 0 &&
        !jigsaw.bottomShow &&
        shouldShowBatchDownload ? (
          <div className="search-result-fixed-actions">
            <button
              type="button"
              className="search-result-download-btn"
              style={{ background: mainColor }}
              onClick={startDownload}
            >
              {props.batchDownloadText || '批量下载图片'}
            </button>
          </div>
        ) : null}
        <SimpleBigPic
          pageType="SearchResult"
          index={current}
          list={list}
          setIndex={setBigIndex}
          loadBigPic={loadBigPic}
          loadFinish={loadFinish}
          showPoster
          onPoster={setPosterImage}
          bigPicTopInfoRender={
            props.showGalleryJumper !== false ||
            (Number(window.configDetail?.use_face) && currentMaybeList.length) ? (
              <div className="search-result-bigpic-extra">
                {props.showGalleryJumper !== false ? (
                  <GalleryJumperWithName gallery={gallery} />
                ) : null}
                {Number(window.configDetail?.use_face) && currentMaybeList.length ? (
                  <div className="maybe-list-wrapper" key={currentGallery?.id}>
                    <div className="maybe-list someone-list" style={{overflow: 'auto'}}>
                      {currentMaybeList.map((maybe: IImageMayBe, index: number) => (
                        <MayBeOneImg
                          key={maybe.personid}
                          maybe={maybe}
                          onClick={(faceurl?: string) => {
                            onPersonClick(index, maybe, faceurl);
                          }}
                          active={activeAvatarIndex === index}
                        />
                      ))}
                    </div>
                    <div className="tip-row">
                      <span style={{ color: '#4EC8BF', marginRight: 5 }}>
                        {intl.get('Click portrait')}
                      </span>
                      {intl.get('to find more of him/her')}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : undefined
          }
        />
        {posterImage ? (
          <PosterPortal
            data={[posterImage]}
            onClose={() => setPosterImage(null)}
          />
        ) : null}
        <AllForBigPicPersonList
          personid={personParams.personid}
          imageid={personParams.imageid}
          faceurl={personParams.faceurl}
          maybe={currentMaybeList}
          onClose={onPersonBack}
        />
      </div>
    </div>,
    containerEl
  );
};

export default inject('rootStore')(observer(SearchResult));
